import { computed, inject, Injectable } from '@angular/core';
import { GlobalsStore } from '../../stores/globals.state';
import { CommuneStore } from '../../stores/commune/commune.state';
import { AtlasConfiguration, Commune } from '../../models/commune';
import { Colorset, fallbackColorset } from '../../models/colorset';
import { Indicator } from '../../models/indicator';
import { ColorsetStore } from '../../stores/colorset/colorset.state';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { IndicatorData } from '../../models/indicator-data';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';

/** Keys of the nested atlasConfig sections that carry their own colorset. */
export type ColorsetDefaultPath =
  | 'map'
  | 'bar'
  | 'line'
  | 'pie'
  | 'polarArea'
  | 'radar';

export type ColorsetDefaultKey = 'general' | ColorsetDefaultPath;

export interface ColorsetDefaultSlot {
  key: ColorsetDefaultKey;
  label: string;
  /** Plural used in fallback hints, e.g. "Kommunen-Standard für Karten". */
  scope: string;
  icon: string;
  /** `null` = root level (`atlasConfig.colorset`) */
  path: ColorsetDefaultPath | null;
}

export const colorsetDefaultSlots: ColorsetDefaultSlot[] = [
  {
    key: 'general',
    label: 'Allgemein',
    scope: 'alle Typen',
    icon: 'fas fa-globe',
    path: null,
  },
  {
    key: 'map',
    label: 'Karten',
    scope: 'Karten',
    icon: 'fas fa-map',
    path: 'map',
  },
  {
    key: 'bar',
    label: 'Balken',
    scope: 'Balkendiagramme',
    icon: 'fas fa-chart-bar',
    path: 'bar',
  },
  {
    key: 'line',
    label: 'Linie',
    scope: 'Liniendiagramme',
    icon: 'fas fa-chart-line',
    path: 'line',
  },
  {
    key: 'pie',
    label: 'Kreis',
    scope: 'Kreisdiagramme',
    icon: 'fas fa-chart-pie',
    path: 'pie',
  },
  {
    key: 'polarArea',
    label: 'Polar',
    scope: 'Polardiagramme',
    icon: 'fas fa-snowflake',
    path: 'polarArea',
  },
  {
    key: 'radar',
    label: 'Radar',
    scope: 'Radardiagramme',
    icon: 'fas fa-satellite-dish',
    path: 'radar',
  },
];

/**
 * Colorset sources of a widget that take precedence over the commune
 * defaults, see {@link ColorsetDefaultsService.resolve}.
 */
export interface ColorsetChain {
  indicatorData?: IndicatorData | null;
  indicator?: Indicator | null;
  /** Colorset chosen on the widget, applies unless the data fixes one. */
  widgetColorset?: string | null;
}

/**
 * The widget config fields read by {@link ColorsetDefaultsService.widgetChain}.
 */
export interface ColorsetWidgetConfig {
  indicator?: string;
  dataLayer?: string;
  colorset?: string | null;
}

const systemFallback: ColorsetFallback = {
  colorset: fallbackColorset,
  source: 'Systemstandard',
};

/**
 * Colorset that takes effect when a colorset field is left empty, together
 * with a human readable description of where it comes from.
 */
export interface ColorsetFallback {
  colorset: Colorset;
  source: string;
}

/**
 * Central read/write access to the commune-wide default colorsets that live
 * in `commune.atlasConfig` (general, map and one per chart type). Used by the
 * colorset administration; the configuration panels write the same fields.
 */
@Injectable({ providedIn: 'root' })
export class ColorsetDefaultsService {
  private readonly globalsStore = inject(GlobalsStore);
  private readonly communeStore = inject(CommuneStore);
  private readonly colorsetStore = inject(ColorsetStore);
  private readonly indicatorStore = inject(IndicatorStore);
  private readonly dataStore = inject(IndicatorDataStore);
  private readonly fallbackCache = new Map<string, ColorsetFallback>();

  readonly slots = colorsetDefaultSlots;

  /**
   * Active commune, preferably taken from the CommuneStore: the configuration
   * panels mutate the commune in place and only the store's `items` signal
   * notifies about that change.
   */
  private readonly commune = computed(() => {
    const active = this.globalsStore.activeCommune();
    if (!active?._id) return active;
    return this.communeStore.items()[active._id] || active;
  });

  /** Current colorset id per slot (undefined = not set). */
  readonly defaults = computed(() => {
    const config = this.commune()?.atlasConfig;
    const result = {} as Record<ColorsetDefaultKey, string | undefined>;
    for (const slot of this.slots) {
      result[slot.key] = this.readSlot(config, slot) || undefined;
    }
    return result;
  });

  /**
   * Returns the slots in which the given colorset is used as default.
   */
  usageOf(colorsetId: string | undefined): ColorsetDefaultSlot[] {
    if (!colorsetId) return [];
    const defaults = this.defaults();
    return this.slots.filter(slot => defaults[slot.key] === colorsetId);
  }

  /** Fallback when even the general commune default is empty. */
  readonly systemFallback = systemFallback;

  /**
   * Fallback for the per-type commune defaults:
   * commune general default -> system default.
   */
  readonly generalFallback = computed(() => this.typeFallback(null));

  /**
   * Fallback for maps without data/indicator colorset:
   * commune map default -> commune general default -> system default.
   */
  readonly mapFallback = computed(() => this.typeFallback('map'));

  /**
   * Commune default for the given widget type: the type's own slot (map,
   * bar, line, pie, polarArea, radar), then the general commune default, then
   * the system default. Types without a slot (e.g. scatter, table) start at
   * the general default.
   */
  typeFallback(type: string | null | undefined): ColorsetFallback {
    const defaults = this.defaults();
    const slot = this.slots.find(item => item.path && item.path === type);
    return (
      (slot &&
        this.fallbackOf(
          defaults[slot.key],
          `Kommunen-Standard für ${slot.scope}`
        )) ||
      this.fallbackOf(defaults.general, 'Kommunen-Standard (Allgemein)') ||
      systemFallback
    );
  }

  /**
   * Colorset fixed by the data: the indicator data's colorset, then the
   * indicator's default, then the default of its parent indicator (which the
   * indicator inherits). `null` if none of them sets one.
   */
  fixedColorset(
    indicatorData: IndicatorData | null | undefined,
    indicator: Indicator | null | undefined
  ): ColorsetFallback | null {
    const fallback = this.fallbackOf(indicatorData?.colorSet, 'Indikatordaten');
    if (fallback) return fallback;

    const parent = indicator?.parent
      ? this.indicatorStore.items()[indicator.parent]
      : undefined;
    for (const candidate of [indicator, parent]) {
      const fallback = this.fallbackOf(
        candidate?.defaultColorSet,
        `Indikator „${candidate?.name}“`
      );
      if (fallback) return fallback;
    }
    return null;
  }

  /**
   * Effective colorset of a widget (or the atlas map). Resolution order:
   * indicator data -> indicator (-> parent indicator) -> widget colorset ->
   * commune default of the type -> general commune default -> system default.
   *
   * Colorsets of the data are fixed and override the widget's colorset.
   * Commune defaults are resolved live, so widgets without an own colorset
   * follow later changes of the commune configuration.
   */
  resolve(
    type: string | null | undefined,
    chain: ColorsetChain
  ): ColorsetFallback {
    return (
      this.fixedColorset(chain.indicatorData, chain.indicator) ||
      this.fallbackOf(chain.widgetColorset, 'Widget') ||
      this.typeFallback(type)
    );
  }

  /**
   * Builds the {@link ColorsetChain} of a widget config: the indicator data
   * and the indicator from `indicator`/`dataLayer`, then its `colorset`.
   * The legacy `map.colorset` (a copy of the commune default made on
   * creation, not editable) is ignored.
   */
  widgetChain(config: ColorsetWidgetConfig | null | undefined): ColorsetChain {
    const indicator = config?.indicator
      ? this.indicatorStore.items()[config.indicator]
      : undefined;
    const indicatorData =
      config?.indicator && config.dataLayer
        ? this.dataStore.getByDataLayerAndIndicator(
            config.dataLayer,
            config.indicator
          )
        : null;
    return { indicatorData, indicator, widgetColorset: config?.colorset };
  }

  /**
   * Colorset that applies when a field below the given indicator stays empty
   * (e.g. its indicator data or one of its children): the indicator's own
   * default, then its parent's default, then {@link mapFallback}.
   */
  indicatorFallback(indicator: Indicator | null | undefined): ColorsetFallback {
    return this.fixedColorset(null, indicator) || this.mapFallback();
  }

  setDefault(key: ColorsetDefaultKey, colorsetId: string | null): void {
    this.applyChanges({ [key]: colorsetId });
  }

  /**
   * Removes the given colorset from every slot it is assigned to,
   * e.g. before the colorset gets deleted.
   */
  clearUsagesOf(colorsetId: string): void {
    const changes: Partial<Record<ColorsetDefaultKey, null>> = {};
    for (const slot of this.usageOf(colorsetId)) changes[slot.key] = null;
    if (Object.keys(changes).length) this.applyChanges(changes);
  }

  /**
   * Merges the slot changes immutably into the active commune's atlasConfig
   * and persists the commune. Nested sections (e.g. `bar`) keep their other
   * settings.
   */
  private applyChanges(
    changes: Partial<Record<ColorsetDefaultKey, string | null>>
  ): void {
    const commune = this.commune();
    if (!commune) return;

    let atlasConfig: AtlasConfiguration = { ...commune.atlasConfig };
    for (const slot of this.slots) {
      if (!(slot.key in changes)) continue;
      const colorset = changes[slot.key] || '';
      atlasConfig = slot.path
        ? {
            ...atlasConfig,
            [slot.path]: { ...atlasConfig[slot.path], colorset },
          }
        : { ...atlasConfig, colorset };
    }

    const updated: Commune = { ...commune, atlasConfig };
    this.globalsStore.patchActiveCommune(updated);
    this.communeStore.update(updated);
  }

  private fallbackOf(
    colorsetId: string | null | undefined,
    source: string
  ): ColorsetFallback | null {
    const colorset = colorsetId
      ? this.colorsetStore.items()[colorsetId]
      : undefined;
    if (!colorset) return null;

    // Stable references: the fallbacks are resolved from templates, a fresh
    // object per change detection run would fail the dev mode check.
    const key = `${source}|${colorset._id}`;
    const cached = this.fallbackCache.get(key);
    if (cached?.colorset === colorset) return cached;
    const fallback = { colorset, source };
    this.fallbackCache.set(key, fallback);
    return fallback;
  }

  private readSlot(
    config: AtlasConfiguration | undefined,
    slot: ColorsetDefaultSlot
  ): string | undefined {
    if (!config) return undefined;
    return slot.path ? config[slot.path]?.colorset : config.colorset;
  }
}
